import { Box, TabItem, TabList, Tabs } from '@razorpay/blade/components';
import { routePaths } from 'components/Routes/data';
import React from 'react';
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { compensationTabItems } from '../constants';
import BonusView from '../views/BonusView';
import RecurringDeductionsView from '../views/RecurringDeductionsView';
import CtcComponent from './CtcComponent';
import { useSearchParams } from 'hooks/useSearchParams';
import PerquisitesView from '../views/PerquisitesView';
import { FlexibleBenefitsView } from '../views/FlexibleBenefitsView';
import IsFeatureDisabledForRearch from 'access/IsFeatureDisabledForRearch';
import RestrictedRoute from 'components/Routes/RestrictedRoute';
import { useSelector } from 'react-redux';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import userRoles from 'constants/userRoles';
import useHasPermission from 'utils/useHasPermission';
import permissions from 'constants/permissions';
import { useReArchBonusEnabled } from 'components/Bonus/components/AllocateBonusModal/hooks/useReArchBonusEnabled';

export const CompensationBreakdown = () => {
  const location = useLocation();
  const history = useHistory();
  const currentTab = location.pathname.split('/')[3];
  const isReArchBonusEnabled = useReArchBonusEnabled();
  const loggedInUserId = useSelector(loggedInUserSelectors.peopleId);
  const isBonusAdmin = useSelector(loggedInUserSelectors.role) === userRoles.ADMIN;
  const hasPayrollEditPermission = useHasPermission([permissions.PAYROLL_EDIT]);
  const { urlParams } = useSearchParams();
  const requestedUserId = urlParams.get('userId');
  const loggedInUserIdString = loggedInUserId?.toString() ?? null;
  // Admins and payroll operators (PAYROLL_EDIT) may view the selected employee;
  // plain employees stay pinned to their own record (ISS-3653128).
  const canManageRequestedUser = isBonusAdmin || hasPayrollEditPermission;
  const peopleId = canManageRequestedUser
    ? requestedUserId || loggedInUserIdString
    : loggedInUserIdString;

  const urlUserId = canManageRequestedUser ? requestedUserId : null;

  const handleTabChange = (value: string) => {
    const searchParams = urlUserId ? `?userId=${urlUserId}` : '';
    history.push({
      pathname:
        !peopleId || peopleId === loggedInUserId?.toString()
          ? `${routePaths.payDetails.compensation.myCompensation.root}/${value}`
          : `${routePaths.payDetails.compensation.root}/${value}`,
      search: searchParams,
    });
  };

  return (
    <>
      <Tabs
        variant="bordered"
        orientation="horizontal"
        size={'medium'}
        onChange={handleTabChange}
        defaultValue={compensationTabItems.ctc}
        value={currentTab}>
        <TabList>
          <TabItem value={compensationTabItems.ctc}>CTC Component</TabItem>
          {isReArchBonusEnabled && <TabItem value={compensationTabItems.bonus}>Bonus</TabItem>}
          <IsFeatureDisabledForRearch feature={['isRecurringDeductionsDisabled']}>
            <TabItem value={compensationTabItems.deductions}>Deductions</TabItem>
          </IsFeatureDisabledForRearch>
          <IsFeatureDisabledForRearch feature={['isPerquisiteDisabled']}>
            <TabItem value={compensationTabItems.perquisites}>Perquisites</TabItem>
          </IsFeatureDisabledForRearch>
          <IsFeatureDisabledForRearch feature={['isM3S2Disabled']}>
            <TabItem value={compensationTabItems.flexi}>Flexi Components</TabItem>
          </IsFeatureDisabledForRearch>
        </TabList>
      </Tabs>

      <Switch>
        <Route path={[routePaths.payDetails.compensation.root]} exact>
          <Redirect
            to={{
              pathname: routePaths.payDetails.compensation.ctc,
              search: urlUserId ? `?userId=${urlUserId}` : '',
            }}
          />
        </Route>
        <Route path={[routePaths.payDetails.compensation.myCompensation.root]} exact>
          <Redirect
            to={{
              pathname: routePaths.payDetails.compensation.myCompensation.ctc,
              search: urlUserId ? `?userId=${urlUserId}` : '',
            }}
          />
        </Route>
        <Route
          path={[
            routePaths.payDetails.compensation.ctc,
            routePaths.payDetails.compensation.myCompensation.ctc,
          ]}
          component={CtcComponent}
          exact
        />
        {isReArchBonusEnabled && (
          <Route
            path={[
              routePaths.payDetails.compensation.bonus,
              routePaths.payDetails.compensation.myCompensation.bonus,
            ]}
            component={BonusView}
            exact
          />
        )}
        <RestrictedRoute
          path={[
            routePaths.payDetails.compensation.deductions,
            routePaths.payDetails.compensation.myCompensation.deductions,
          ]}
          exact
          component={RecurringDeductionsView}
          disabledFeaturesForRearch={['isRecurringDeductionsDisabled']}
        />
        <RestrictedRoute
          path={[
            routePaths.payDetails.compensation.perquisites,
            routePaths.payDetails.compensation.myCompensation.perquisites,
          ]}
          exact
          component={PerquisitesView}
          disabledFeaturesForRearch={['isPerquisiteDisabled']}
        />
        <RestrictedRoute
          path={[
            routePaths.payDetails.compensation.flexi,
            routePaths.payDetails.compensation.myCompensation.flexi,
          ]}
          exact
          component={FlexibleBenefitsView}
          disabledFeaturesForRearch={['isM3S2Disabled']}
        />
      </Switch>
      <Box marginBottom="spacing.8" />
    </>
  );
};
